/* =========================================================================
 *  FMColourPicker — custom in-page colour picker
 *  ---------------------------------------------
 *  Replaces the OS-native `<input type="color">` dialog used by the logo-mat
 *  wizard form so we can embed a "Use Selected Colour" button inside the
 *  picker UI itself. Pure HTML / CSS / JS — no external library.
 *
 *  Markup contract (rendered by sections/main-product.liquid):
 *    `#fmColourPicker`                 — outer dialog (hidden by default)
 *    `[data-fm-cp-close]`              — close triggers (× button + overlay)
 *    `[data-fm-cp-sv]`                 — saturation/value square
 *    `[data-fm-cp-sv-thumb]`           — SV draggable thumb
 *    `[data-fm-cp-hue]`                — hue strip
 *    `[data-fm-cp-hue-thumb]`          — hue draggable thumb
 *    `[data-fm-cp-hex]` / `-r/-g/-b`   — text/number inputs
 *    `[data-fm-cp-preview]`            — preview swatch
 *    `[data-fm-cp-apply]`              — "Use Selected Colour" button
 *
 *  Public API (exposed as `window.FMColourPicker`):
 *    `.open(seedHex, onCommit)` — open the picker seeded with `seedHex`
 *                                  ("FF0000" or "#FF0000"). When the
 *                                  customer clicks "Use Selected Colour",
 *                                  `onCommit("AABBCC")` is called.
 *    `.close()`                  — close the picker without committing.
 *
 *  HSV ↔ RGB ↔ HEX conversion is the standard CSS / web-colour math
 *  (no rounding hacks, alpha not supported — opaque colours only).
 * ========================================================================= */
(function () {
  'use strict';

  /* ----------------------------------------------------------------
   *  Colour math helpers
   * ---------------------------------------------------------------- */
  function hsvToRgb(h, s, v) {
    /* h: 0–360, s: 0–1, v: 0–1 → {r, g, b} 0–255 */
    var c = v * s;
    var hp = h / 60;
    var x = c * (1 - Math.abs((hp % 2) - 1));
    var r1 = 0, g1 = 0, b1 = 0;
    if      (hp >= 0 && hp < 1) { r1 = c; g1 = x; b1 = 0; }
    else if (hp >= 1 && hp < 2) { r1 = x; g1 = c; b1 = 0; }
    else if (hp >= 2 && hp < 3) { r1 = 0; g1 = c; b1 = x; }
    else if (hp >= 3 && hp < 4) { r1 = 0; g1 = x; b1 = c; }
    else if (hp >= 4 && hp < 5) { r1 = x; g1 = 0; b1 = c; }
    else if (hp >= 5 && hp < 6) { r1 = c; g1 = 0; b1 = x; }
    var m = v - c;
    return {
      r: Math.round((r1 + m) * 255),
      g: Math.round((g1 + m) * 255),
      b: Math.round((b1 + m) * 255)
    };
  }
  function rgbToHsv(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var d = max - min;
    var h;
    if (d === 0)         h = 0;
    else if (max === r)  h = 60 * (((g - b) / d) % 6);
    else if (max === g)  h = 60 * (((b - r) / d) + 2);
    else                 h = 60 * (((r - g) / d) + 4);
    if (h < 0) h += 360;
    var s = max === 0 ? 0 : d / max;
    var v = max;
    return { h: h, s: s, v: v };
  }
  function hexToRgb(hex) {
    hex = (hex || '').replace(/^#/, '');
    if (hex.length === 3) hex = hex.split('').map(function (c) { return c + c; }).join('');
    if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return null;
    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16)
    };
  }
  function rgbToHex(r, g, b) {
    function pad(n) { var s = Math.max(0, Math.min(255, n | 0)).toString(16).toUpperCase(); return s.length === 1 ? '0' + s : s; }
    return pad(r) + pad(g) + pad(b);
  }

  /* ----------------------------------------------------------------
   *  Picker controller
   * ---------------------------------------------------------------- */
  var dialog, panel, sv, svThumb, hue, hueThumb, hexInput, rIn, gIn, bIn, preview, applyBtn;
  var hsv = { h: 0, s: 1, v: 1 };
  var onCommitCb = null;
  var lastTrigger = null;

  function init() {
    dialog   = document.getElementById('fmColourPicker');
    if (!dialog) return false;
    panel    = dialog.querySelector('.fm-color-picker__panel');
    sv       = dialog.querySelector('[data-fm-cp-sv]');
    svThumb  = dialog.querySelector('[data-fm-cp-sv-thumb]');
    hue      = dialog.querySelector('[data-fm-cp-hue]');
    hueThumb = dialog.querySelector('[data-fm-cp-hue-thumb]');
    hexInput = dialog.querySelector('[data-fm-cp-hex]');
    rIn      = dialog.querySelector('[data-fm-cp-r]');
    gIn      = dialog.querySelector('[data-fm-cp-g]');
    bIn      = dialog.querySelector('[data-fm-cp-b]');
    preview  = dialog.querySelector('[data-fm-cp-preview]');
    applyBtn = dialog.querySelector('[data-fm-cp-apply]');

    /* Close triggers — backdrop overlay + × button + ESC */
    dialog.querySelectorAll('[data-fm-cp-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !dialog.hasAttribute('hidden')) {
        e.preventDefault();
        close();
      }
    });

    /* SV / hue drag handlers */
    bindDrag(sv, function (x, y, w, h) {
      hsv.s = Math.max(0, Math.min(1, x / w));
      hsv.v = Math.max(0, Math.min(1, 1 - y / h));
      syncFromHsv();
    });
    bindDrag(hue, function (x, _y, w) {
      hsv.h = Math.max(0, Math.min(360, (x / w) * 360));
      syncFromHsv();
    });

    /* Text input handlers */
    if (hexInput) {
      hexInput.addEventListener('input', function () {
        /* Strip non-hex chars; uppercase; cap at 6 */
        var v = (hexInput.value || '').replace(/[^0-9A-Fa-f]/g, '').slice(0, 6).toUpperCase();
        if (v !== hexInput.value) hexInput.value = v;
        if (v.length === 6) syncFromHex(v);
      });
    }
    [rIn, gIn, bIn].forEach(function (el) {
      if (!el) return;
      el.addEventListener('input', function () {
        var r = clamp255(parseInt(rIn.value, 10));
        var g = clamp255(parseInt(gIn.value, 10));
        var b = clamp255(parseInt(bIn.value, 10));
        syncFromRgb(r, g, b);
      });
    });

    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        var hex = currentHex();
        var cb = onCommitCb;
        close();
        if (typeof cb === 'function') cb(hex);
      });
    }

    return true;
  }

  function clamp255(n) { if (isNaN(n)) return 0; return Math.max(0, Math.min(255, n)); }

  /* ----------------------------------------------------------------
   *  Drag binding (shared between SV square and hue strip)
   *
   *  Pointer Events cover both mouse + touch in one path. We register
   *  on the element for `pointerdown`, then move/up handlers attach
   *  to the document so a drag continues even when the cursor leaves
   *  the control. `setPointerCapture` keeps the events flowing to the
   *  same element on Chrome/Edge; the document fallback covers Safari.
   * ---------------------------------------------------------------- */
  function bindDrag(el, onUpdate) {
    if (!el) return;
    var dragging = false;

    function update(clientX, clientY) {
      var rect = el.getBoundingClientRect();
      var x = clientX - rect.left;
      var y = clientY - rect.top;
      onUpdate(x, y, rect.width, rect.height);
    }
    function onDown(e) {
      dragging = true;
      el.setPointerCapture && el.setPointerCapture(e.pointerId);
      update(e.clientX, e.clientY);
      e.preventDefault();
    }
    function onMove(e) {
      if (!dragging) return;
      update(e.clientX, e.clientY);
      e.preventDefault();
    }
    function onUp(e) {
      if (!dragging) return;
      dragging = false;
      try { el.releasePointerCapture && el.releasePointerCapture(e.pointerId); } catch (_e) {}
    }
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    /* Document-level fallback for Safari which sometimes drops events
       when the pointer leaves the element bounds during a drag. */
    document.addEventListener('pointermove', function (e) { if (dragging) update(e.clientX, e.clientY); });
    document.addEventListener('pointerup',   function ()  { dragging = false; });
  }

  /* ----------------------------------------------------------------
   *  State sync — three "from X" entry points so any input source
   *  (drag, hex, RGB) flows through to update every other display.
   * ---------------------------------------------------------------- */
  function syncFromHsv()           { renderAll(); }
  function syncFromRgb(r, g, b)    { hsv = rgbToHsv(r, g, b); renderAll(); }
  function syncFromHex(hex)        { var rgb = hexToRgb(hex); if (!rgb) return; syncFromRgb(rgb.r, rgb.g, rgb.b); }

  function currentHex() {
    var rgb = hsvToRgb(hsv.h, hsv.s, hsv.v);
    return rgbToHex(rgb.r, rgb.g, rgb.b);
  }

  function renderAll() {
    var rgb = hsvToRgb(hsv.h, hsv.s, hsv.v);
    var hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    var huePure = hsvToRgb(hsv.h, 1, 1);

    /* SV square: background = pure hue, pseudos draw white→transparent
       (left → right) and transparent→black (top → bottom). */
    if (sv) {
      sv.style.backgroundColor = 'rgb(' + huePure.r + ',' + huePure.g + ',' + huePure.b + ')';
    }
    if (svThumb) {
      svThumb.style.left = (hsv.s * 100) + '%';
      svThumb.style.top  = ((1 - hsv.v) * 100) + '%';
      /* Outline contrast: white thumb with dark border on light colours,
         dark thumb on dark colours. We pick based on the V component. */
      svThumb.style.borderColor = hsv.v > 0.65 ? '#222329' : '#ffffff';
    }
    if (hueThumb) {
      hueThumb.style.left = ((hsv.h / 360) * 100) + '%';
    }
    if (preview) {
      preview.style.backgroundColor = '#' + hex;
    }

    /* Update text inputs only if focus is elsewhere — typing into the
       input would otherwise be overwritten while the user is mid-edit. */
    if (hexInput && document.activeElement !== hexInput) hexInput.value = hex;
    if (rIn && document.activeElement !== rIn) rIn.value = rgb.r;
    if (gIn && document.activeElement !== gIn) gIn.value = rgb.g;
    if (bIn && document.activeElement !== bIn) bIn.value = rgb.b;
  }

  /* ----------------------------------------------------------------
   *  Public API
   * ---------------------------------------------------------------- */
  function open(seedHex, onCommit) {
    if (!dialog) return;
    onCommitCb = onCommit;
    lastTrigger = document.activeElement;
    var rgb = hexToRgb(seedHex) || { r: 255, g: 0, b: 0 };
    hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
    /* Pure-red has saturation but no hue gradient yet; force h=0 to
       guarantee renderAll has a sensible starting hue background. */
    if (isNaN(hsv.h)) hsv.h = 0;
    renderAll();
    dialog.removeAttribute('hidden');
    /* Move focus into the panel for a11y. The Apply button is the
       safest landing spot — always present, primary action. */
    if (applyBtn) applyBtn.focus({ preventScroll: true });
    var trigger = document.getElementById('backgroundCustomColourTrigger');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }
  function close() {
    if (!dialog || dialog.hasAttribute('hidden')) return;
    dialog.setAttribute('hidden', '');
    onCommitCb = null;
    var trigger = document.getElementById('backgroundCustomColourTrigger');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus({ preventScroll: true });
    }
  }

  window.FMColourPicker = { open: open, close: close };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
